import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { trackEvent } from '../utils/analytics';
import { useSiteSettings } from './SiteSettingsContext';

const CartContext = createContext();

const SETTINGS_DEFAULTS = {
  shippingCost: '300.00',
  freeShippingThreshold: '0',
  taxRate: '20',
  taxIncludedInPrice: true,
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const CART_KEY = 'cart';

function readStoredCart() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(CART_KEY) || '[]');
    return Array.isArray(saved) ? saved.filter((item) => item && item.id != null && item.quantity > 0) : [];
  } catch {
    return []; // unreadable or storage blocked
  }
}

function writeStoredCart(cart) {
  try {
    window.localStorage.setItem(CART_KEY, JSON.stringify(cart));
  } catch {
    /* storage full or blocked (some private windows): the cart lasts this visit */
  }
}

export const CartProvider = ({ children }) => {
  const { currency } = useSiteSettings();
  const [cart, setCartState] = useState(readStoredCart);

  // The cart lives in localStorage. It's written in the same step as the
  // change (not in a later effect), so a refresh or a navigation straight
  // after "Buy now" can't come back to an older cart; and other tabs follow
  // along, so a tab left open with an old cart can't write it back over a
  // newer one.
  const setCart = useCallback((update) => {
    setCartState((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;
      writeStoredCart(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === CART_KEY) setCartState(readStoredCart());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [siteSettings, setSiteSettings] = useState(SETTINGS_DEFAULTS);
  const autoCloseTimer = useRef(null);
  const toastTimer = useRef(null);
  const openedByAdd = useRef(false);

  const showToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastVisible(true);
    toastTimer.current = setTimeout(() => {
      setToastVisible(false);
      toastTimer.current = null;
    }, 3000);
  }, []);

  const hideToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = null;
    setToastVisible(false);
  }, []);

  useEffect(() => {
    api.get('/api/settings/shipping')
      .then(res => {
        if (res.data) setSiteSettings(prev => ({ ...prev, ...res.data }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const openCartManually = useCallback((open) => {
    if (autoCloseTimer.current) {
      clearTimeout(autoCloseTimer.current);
      autoCloseTimer.current = null;
    }
    openedByAdd.current = false;
    setIsCartOpen(open);
  }, []);

  const addToCart = (product, quantity = 1, variant = null, options = {}) => {
    const { skipSidebar = false, skipToast = false } = options;
    // GA4 e-commerce event — uses the GA4 recommended schema so the
    // built-in Monetization > E-commerce reports populate without any
    // manual config in the GA4 UI. Fires regardless of cart context
    // (new line vs quantity bump) because GA4 treats every add_to_cart
    // as a discrete intent signal.
    const unitPrice = parseFloat(variant?.price ?? product.price) || 0;
    trackEvent('add_to_cart', {
      currency: currency || 'USD',
      value: unitPrice * quantity,
      items: [{
        item_id: variant?.sku ?? product.sku ?? String(product.id),
        product_id: product.id,
        item_name: product.name,
        item_category: product.category?.name || product.category,
        price: unitPrice,
        quantity,
      }],
    });

    setCart(prevCart => {
      const cartKey = variant?.id ? `${product.id}-${variant.id}` : String(product.id);
      const existingItem = prevCart.find(item => (item.cartKey || String(item.id)) === cartKey);

      if (existingItem) {
        return prevCart.map(item =>
          (item.cartKey || String(item.id)) === cartKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        const line = {
          id: product.id,
          name: product.name,
          slug: product.slug,
          price: variant?.price ?? product.price,
          sku: variant?.sku ?? product.sku,
          image: variant?.image || (product.images && product.images[0] ? product.images[0] : null),
          quantity,
          availabilityStatus: product.availabilityStatus || 'active',
        };
        if (variant?.id) {
          line.cartKey = cartKey;
          line.variantId = variant.id;
          line.selectedOptions = variant.options || {};
        }
        return [...prevCart, line];
      }
    });
    
    if (!skipToast) showToast();
    if (!skipSidebar) {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
      openedByAdd.current = true;
      setIsCartOpen(true);
      autoCloseTimer.current = setTimeout(() => {
        if (openedByAdd.current) {
          setIsCartOpen(false);
          openedByAdd.current = false;
        }
        autoCloseTimer.current = null;
      }, 4000);
    }
  };

  const removeFromCart = (cartKey) => {
    setCart(prevCart => prevCart.filter(item => (item.cartKey || String(item.id)) !== cartKey));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity < 1) {
      removeFromCart(cartKey);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        (item.cartKey || String(item.id)) === cartKey ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + (parseFloat(item.price) * item.quantity);
    }, 0).toFixed(2);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getCartSubtotal = () => {
    return getCartTotal();
  };

  const getEffectiveShipping = useCallback((subtotal) => {
    const sub = parseFloat(subtotal) || 0;
    const threshold = parseFloat(siteSettings.freeShippingThreshold) || 0;
    if (threshold > 0 && sub >= threshold) return '0.00';
    return siteSettings.shippingCost;
  }, [siteSettings]);

  const calculateTax = useCallback((subtotal) => {
    const sub = parseFloat(subtotal) || 0;
    const rate = (parseFloat(siteSettings.taxRate) || 0) / 100;
    if (siteSettings.taxIncludedInPrice) {
      return (sub - sub / (1 + rate)).toFixed(2);
    }
    return (sub * rate).toFixed(2);
  }, [siteSettings]);

  const calculateTotal = useCallback((subtotal, shippingOverride) => {
    const sub = parseFloat(subtotal) || 0;
    const ship = parseFloat(shippingOverride ?? getEffectiveShipping(subtotal)) || 0;
    if (siteSettings.taxIncludedInPrice) {
      return (sub + ship).toFixed(2);
    }
    const tax = parseFloat(calculateTax(subtotal)) || 0;
    return (sub + ship + tax).toFixed(2);
  }, [siteSettings, getEffectiveShipping, calculateTax]);

  const value = {
    cart,
    isCartOpen,
    setIsCartOpen: openCartManually,
    toastVisible,
    hideToast,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    getCartSubtotal,
    getEffectiveShipping,
    calculateTax,
    calculateTotal,
    siteSettings,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

